/* eslint-disable @next/next/no-img-element */
import { Button, Skeleton, useMediaQuery } from '@mui/material';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import { postJoinGroup } from '../../../../apis/tms/feed.api';
import { convertQuantity } from '../../../../common/quantityHelpers';
import { PATH_NAME } from '../../../../configs/pathName';
import {
  FEED_GROUPS_SUGGESTION,
  USER_JOINED_GROUPS_USER_WALL,
} from '../../../../constant/QueryKeyName';
import { ROLE_ACCESS } from '../../../../constant/storage';
import {
  FolderStatusEnum,
  GroupSuggestionActionEnum,
} from '../../../../interfaces/training.interface';
import RequestJoinGroupButton from '../../../buttons/training-module/RequestJoinGroupButton';
import {
  JoinButtonDesktop,
  JoinButtonMobile,
} from './SideGroupSuggestionButton';

export interface IGroupSuggestion {
  groupId: string;
  name: string;
  numOfMembers: number;
  avatar: string | null;
  orientation?: 'vertical' | 'horizontal';
  status?: FolderStatusEnum;
  inSearch?: boolean;
  disableRedirect?: boolean;
  action?: GroupSuggestionActionEnum;
  role?: ROLE_ACCESS;
  isJoined?: boolean;
}

const GroupSuggestion = ({
  groupId,
  name,
  numOfMembers,
  avatar,
  orientation = 'horizontal',
  status,
  role,
  inSearch = false,
  toast,
  disableRedirect = false,
  action = GroupSuggestionActionEnum.JoinGroup,
  isJoined,
}: IGroupSuggestion & {
  toast: (_content: string, _type: 'success' | 'error') => void;
}) => {
  const matchesLg = useMediaQuery('(min-width:1024px)');
  const matchesSm = useMediaQuery('(min-width:640px)');
  const [joined, setJoined] = useState(isJoined);
  const router = useRouter();
  const queryClient = useQueryClient();

  const handleJoinGroup = async () => {
    try {
      await postJoinGroup(groupId, FolderStatusEnum.Public);
      setJoined(true);
      queryClient.refetchQueries([USER_JOINED_GROUPS_USER_WALL]);
      queryClient.refetchQueries([FEED_GROUPS_SUGGESTION]);
      toast('Tham gia nhóm thành công.', 'success');
    } catch (error) {
      toast('Có lỗi xảy ra.', 'error');
      console.log(error);
    }
  };

  const handleClick = () => {
    if (!joined) {
      handleJoinGroup();
    }
  };

  const handleRedirect = () => {
    router.push(`${PATH_NAME.MY_GROUPS}/${groupId}`);
  };

  const renderStatusIcon = (status: FolderStatusEnum | undefined) => {
    if (status === FolderStatusEnum.Private)
      return (
        <Image
          alt="private"
          className=""
          src="/icons/private.svg"
          width={15}
          height={15}
        />
      );
    if (status === FolderStatusEnum.Public)
      return (
        <Image
          alt="public"
          className=""
          src="/icons/public.svg"
          width={15}
          height={15}
        />
      );
  };

  const joinPublicButtonMapper: { [key: string]: React.ReactNode } = {
    mobile: (
      <JoinButtonMobile
        handleClick={handleClick}
        groupId={groupId}
        action={action}
        handleJoinGroup={handleJoinGroup}
        joined={joined}
        role={role}
      />
    ),
    desktop: (
      <JoinButtonDesktop
        handleClick={handleClick}
        groupId={groupId}
        action={action}
        handleJoinGroup={handleJoinGroup}
        joined={joined}
        role={role}
        inSearch={inSearch}
      />
    ),
  };

  return (
    <>
      <div className="w-full">
        {orientation == 'horizontal' ? (
          <DivWrapper
            onClick={disableRedirect ? undefined : handleRedirect}
            widthClassname={matchesLg ? 'w-[257px] xl:w-auto' : 'w-full !ml-0'}
            desktopMode={inSearch || matchesLg}
          >
            <div>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-3 md:flex-col md:items-start lg:flex-row lg:items-center">
                <div className="relative flex">
                  {avatar ? (
                    <Image
                      src={avatar}
                      className={'rounded-full object-cover'}
                      alt="avatar"
                      width={32}
                      height={32}
                    />
                  ) : (
                    <img
                      src={'/icons/default-group-avatar.svg'}
                      width={32}
                      height={32}
                      alt="avatar"
                    />
                  )}
                </div>
                {status === FolderStatusEnum.Private && !isJoined ? (
                  <div onClick={(e) => e.stopPropagation()}>
                    <RequestJoinGroupButton
                      initContent={'Tham gia'}
                      undoContent={'Hủy yêu cầu'}
                      groupId={groupId}
                      groupPrivacyStatus={FolderStatusEnum.Private}
                      className="!h-full !w-full !flex-1 !rounded-[20px] !px-[10px] !py-[7px] !text-[12px] !font-semibold !leading-[16px] !shadow-none"
                      variant={'outlined'}
                      containerClassname="w-auto"
                      size="h-auto w-auto min-h-[30px] min-w-[74px] !rounded-[20px]"
                      toastOutside={toast}
                    />
                  </div>
                ) : (
                  joinPublicButtonMapper[`${matchesSm ? 'desktop' : 'mobile'}`]
                )}
              </div>
              <div className="flex flex-col">
                <p className="truncate text-sm font-semibold leading-[160%] tracking-[-0.4px] text-[#242424]">
                  {name}
                </p>
                {numOfMembers !== undefined ? (
                  <span className="inline-flex h-fit items-center justify-start gap-1 text-xs font-medium leading-[140%] text-[#7C7C7C]">
                    {renderStatusIcon(status)}
                    {convertQuantity(numOfMembers)} thành viên
                  </span>
                ) : null}
              </div>
            </div>
          </DivWrapper>
        ) : (
          <DivWrapper
            onClick={disableRedirect ? undefined : handleRedirect}
            widthClassname="sm:w-[167px]"
            heightClassname="h-[198px]"
            customClassname="p-2"
            disableShadow
          >
            <div className="flex h-full flex-col justify-between">
              <div>
                <div className="mb-2 flex items-center justify-center">
                  <div className="relative flex">
                    {avatar ? (
                      <Image
                        src={avatar}
                        className={'rounded-full object-cover'}
                        alt="avatar"
                        width={64}
                        height={64}
                      />
                    ) : (
                      <img
                        src={'/icons/default-group-avatar.svg'}
                        width={64}
                        height={64}
                        alt="avatar"
                      />
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-1 text-center">
                  <p className="line-clamp-2 text-sm font-semibold leading-[160%] tracking-[-0.4px] text-[#242424]">
                    {name}
                  </p>
                  {numOfMembers !== undefined ? (
                    <span className="inline-flex h-fit items-center justify-center gap-1 text-xs font-medium leading-[140%] text-[#7C7C7C]">
                      {renderStatusIcon(status)}
                      {convertQuantity(numOfMembers)} thành viên
                    </span>
                  ) : null}
                </div>
              </div>

              <Button
                style={{ textTransform: 'none', color: '#242424' }}
                className="w-full rounded-md border-gray-300 !py-1 enabled:!bg-primary_blue_bg disabled:!bg-gray-200 disabled:!text-gray-500"
                variant="contained"
                disableElevation
                onClick={handleClick}
                disabled={joined}
              >
                {joined ? 'Đã tham gia' : 'Tham gia'}
              </Button>
            </div>
          </DivWrapper>
        )}
      </div>
    </>
  );
};

function DivWrapper({
  children,
  widthClassname,
  heightClassname,
  customClassname,
  disableShadow = false,
  desktopMode = false,
  onClick = undefined,
}: {
  widthClassname?: string;
  heightClassname?: string;
  customClassname?: string;
  children?: React.ReactNode | React.ReactNode[];
  disableShadow?: boolean;
  desktopMode?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement> | undefined;
}) {
  return (
    <div
      className={`${onClick != undefined && 'cursor-pointer'} bg-transparent`}
      onClick={onClick && onClick}
    >
      <div
        className={`flex flex-col bg-white p-3 ${
          disableShadow
            ? 'border border-[#E9E9E9]'
            : // margin to save space for shadow
              'shadow-[0px_10px_20px_rgba(0,0,0,0.05)] '
        } rounded-lg ${widthClassname} ${heightClassname} ${customClassname} ${
          desktopMode ? 'm-[0px_16px_8px]' : 'm-[20px_16px_40px]'
        }`}
      >
        {children}
      </div>
    </div>
  );
}

export const GroupSuggestionSkeleton = ({
  orientation = 'horizontal',
}: {
  orientation?: 'vertical' | 'horizontal';
}) => {
  const matchesLg = useMediaQuery('(min-width:1024px)');

  return (
    <div>
      {orientation == 'horizontal' ? (
        <DivWrapper
          widthClassname={matchesLg ? 'w-[257px]' : 'w-full !ml-0'}
          desktopMode={matchesLg}
        >
          <div className="mb-2 flex items-center justify-between">
            <div className="relative flex">
              <Skeleton
                animation="wave"
                className="relative z-[0] transform"
                variant="circular"
                width={32}
                height={32}
              />
            </div>
            <Skeleton
              animation="wave"
              variant="rounded"
              className="!rounded-2xl"
              width={74}
              height={30}
            />
          </div>
          <div>
            <Skeleton
              animation="wave"
              variant="text"
              sx={{ fontSize: '14px' }}
            />
            <Skeleton
              animation="wave"
              variant="text"
              sx={{ fontSize: '12px' }}
            />
          </div>
        </DivWrapper>
      ) : (
        <DivWrapper
          widthClassname="sm:w-[167px]"
          heightClassname="h-[198px]"
          customClassname="p-2"
          disableShadow
        >
          <div className="flex h-full flex-col justify-between">
            <div>
              <div className="mb-2 flex items-center justify-center">
                <div className="relative flex">
                  <Skeleton
                    animation="wave"
                    className="relative z-[0] transform"
                    variant="circular"
                    width={64}
                    height={64}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1 text-center">
                <Skeleton
                  animation="wave"
                  variant="text"
                  sx={{ fontSize: '14px' }}
                />
                <Skeleton
                  animation="wave"
                  variant="text"
                  sx={{ fontSize: '12px' }}
                />
              </div>
            </div>
            <Skeleton
              animation="wave"
              variant="rounded"
              className="!rounded-md"
              height={30}
            />
          </div>
        </DivWrapper>
      )}
    </div>
  );
};

// eslint-disable-next-line no-unused-vars
export const roleLabelMapper: { [key in ROLE_ACCESS]: string } = {
  [ROLE_ACCESS.Owner]: 'Chủ sở hữu',
  [ROLE_ACCESS.Editor]: 'Biên tập viên',
  [ROLE_ACCESS.Viewer]: 'Thành viên',
  [ROLE_ACCESS.Revoked]: '',
  [ROLE_ACCESS.Requested]: '',
};
export { DivWrapper };
export default GroupSuggestion;
